import { useEffect, useState } from 'react';
import {
  useChainInFlight,
  useChainNext,
  useRefreshSessions,
  useRunResult,
  useSession,
} from '../../lib/workflows.hooks.js';
import { AI_INSIGHTS } from './aiInsightsOffer.js';
import type { Session, WorkflowRun } from './workflows.types.js';

const UNREADABLE = 'This run could not be read.';
const NO_SELECTION: InsightsSelection = { items: [], positions: [] };

export interface AiInsights {
  open: boolean;
  run: WorkflowRun | null;
  pending: boolean;
  error: string | null;
  toggle: () => void;
  close: () => void;
  retry: () => void;
}

// The parent's rows the insight runs on; no items runs on all of them, which `positions`
// still names so a stored insight can be matched to it.
export interface InsightsSelection {
  items: Record<string, unknown>[];
  positions: number[];
}

interface InsightsState {
  // The parent run and the picks, as one key: other picks are another insight.
  key: string | null;
  open: boolean;
  // The run this page started; a reload finds it again as the session's aside.
  runId: string | null;
  started: boolean;
}

type Aside = Session['asides'][number];

function samePositions(stored: number[] | null, wanted: number[]): boolean {
  if (stored === null) return true;
  const set = new Set(stored);
  return set.size === new Set(wanted).size && wanted.every((position) => set.has(position));
}

function newestAside(
  asides: Aside[],
  parentRunId: string | null,
  positions: number[],
): Aside | null {
  const own = asides.filter(
    (aside) =>
      aside.actionType === AI_INSIGHTS &&
      aside.triggeredByRunId === parentRunId &&
      samePositions(aside.selectedPositions, positions),
  );
  return own.reduce<Aside | null>(
    (newest, aside) => (newest === null || aside.startedAt > newest.startedAt ? aside : newest),
    null,
  );
}

// One insight per step and selection: reopening, reloading or coming back reads the one the
// session already holds; changed picks close the panel and the next open starts a new run.
export function useAiInsights(
  parentRunId: string | null,
  selection: InsightsSelection = NO_SELECTION,
): AiInsights {
  const chainNext = useChainNext();
  const key = parentRunId === null ? null : `${parentRunId}|${selection.positions.join(',')}`;
  const fresh: InsightsState = { key, open: false, runId: null, started: false };
  const [stored, setStored] = useState<InsightsState>(fresh);
  const state = stored.key === key ? stored : fresh;
  const session = useSession(state.open ? parentRunId : null);
  const aside = newestAside(session.data?.asides ?? [], parentRunId, selection.positions);
  const runId = state.runId ?? aside?.runId ?? null;
  const run = useRunResult(state.open ? runId : null);
  const inFlight = useChainInFlight(parentRunId, AI_INSIGHTS);
  // Only a settled, successful read, with no request still out, shows the session holds no
  // insight yet: a failed read keeps its older cached data.
  const settled =
    session.data !== undefined && !session.isFetching && !session.isError && !inFlight;

  const start = (): void => {
    if (parentRunId === null) return;
    setStored({ ...state, open: true, runId: null, started: true });
    chainNext.mutate(
      {
        runId: parentRunId,
        actionType: AI_INSIGHTS,
        selectItems: selection.items,
        ...(selection.items.length === 0 ? { selectTop: 'all' as const } : {}),
        paramsOverride: {},
      },
      {
        onSuccess: ({ runId: started }) => {
          setStored((current) => (current.key === key ? { ...current, runId: started } : current));
        },
      },
    );
  };

  const shouldStart = state.open && !state.started && settled && runId === null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: start is rebuilt every render; `started` makes this fire once per step
  useEffect(() => {
    if (shouldStart) start();
  }, [shouldStart]);

  // The page lists the insight among its outcomes once it lands.
  const landed = run.data?.status === 'completed' ? run.data.runId : null;
  const refreshSessions = useRefreshSessions();
  useEffect(() => {
    if (landed !== null) refreshSessions();
  }, [landed, refreshSessions]);

  const close = (): void => setStored({ ...state, open: false });
  const toggle = (): void => (state.open ? close() : setStored({ ...state, open: true }));
  const record = run.data ?? null;
  // A failed request may still have created the run; once the session shows it, that run counts.
  const submitError = runId === null ? (chainNext.error?.message ?? null) : null;
  const readFailed = run.isError || (session.isError && runId === null);
  const error = submitError ?? (readFailed ? UNREADABLE : null);
  // A new paid run only replaces one known to have failed; anything else is read again.
  const retry = (): void => {
    if (settled && (record?.status === 'failed' || submitError !== null)) {
      start();
      return;
    }
    void session.refetch();
    if (runId !== null) void run.refetch();
  };

  return {
    open: state.open,
    run: record,
    pending: chainNext.isPending || inFlight || (error === null && record === null),
    error,
    toggle,
    close,
    retry,
  };
}
